import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import axios from "axios";
import MCQManager from "./MCQManager";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const MCQGroupedView = () => {
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMCQ, setEditMCQ] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [openYear, setOpenYear] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
const [uploadedDates, setUploadedDates] = useState([]);

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    fetchGroupedMCQs();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
  const dates = [];

  Object.entries(groupedData).forEach(([year, months]) => {
    Object.entries(months).forEach(([month, days]) => {
      Object.keys(days).forEach((day) => {
        const d = new Date(`${month} ${day}, ${year}`);
        dates.push(d.toDateString());
      });
    });
  });

  setUploadedDates(dates);

}, [groupedData]);

useEffect(() => {
  if (!selectedDate) return;

  const year = selectedDate.getFullYear().toString();

  const month = selectedDate.toLocaleString("default", {
    month: "long",
  });

  const day = selectedDate.getDate().toString();

  setOpenYear(year);
  setOpenMonth(month);
  setOpenDay(day);

  setTimeout(() => {
    const el = document.getElementById(selectedDate.toDateString());

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, 300);

}, [selectedDate]);

  const fetchGroupedMCQs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/mcqs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroupedData(res.data || {});
    } catch (error) {
      console.error("Failed to fetch MCQs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this MCQ?")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/mcqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchGroupedMCQs();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const renderMCQDetails = (mcq) => (
    <div className="
rounded-3xl
border
border-gray-200
dark:border-gray-700
bg-white/90
dark:bg-gray-800/90
backdrop-blur-xl
shadow-lg
hover:shadow-2xl
transition-all
duration-300
p-6
text-gray-900
dark:text-gray-100
">
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
        <span className="font-semibold">Type:</span> {mcq.type}
      </div>

      {mcq.question && (
        <div className="mb-2">
          <span className="font-bold">Q:</span> {mcq.question}
        </div>
      )}

      {/* Options */}
      {mcq.options && mcq.options.length > 0 && mcq.options.some(Boolean) && (
        <div className="mb-2">
          <span className="font-semibold">Options:</span>
          <ul className="list-disc ml-6 text-base">
            {mcq.options.map((opt, idx) => opt && <li key={idx}>{opt}</li>)}
          </ul>
        </div>
      )}

      {/* Statements */}
      {mcq.type === "statement" && mcq.statements?.some(Boolean) && (
        <div className="mb-2">
          <span className="font-semibold">Statements:</span>
          <ul className="list-decimal ml-6 text-base">
            {mcq.statements.filter(Boolean).map((stmt, idx) => (
              <li key={idx}>{stmt}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Matching */}
      {mcq.type === "matching" && mcq.matchingA?.some(Boolean) && mcq.matchingB?.some(Boolean) && (
        <div className="mb-2 overflow-x-auto">
          <span className="font-semibold">Matching:</span>
          <table className="min-w-[200px] border-collapse border mt-1 text-base">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="border px-2 py-1">Column A</th>
                <th className="border px-2 py-1">Column B</th>
              </tr>
            </thead>
            <tbody>
              {mcq.matchingA.map((a, idx) =>
                a || mcq.matchingB[idx] ? (
                  <tr key={idx} className="even:bg-gray-50 dark:even:bg-gray-700">
                    <td className="border px-2 py-1">{a}</td>
                    <td className="border px-2 py-1">{mcq.matchingB[idx]}</td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Assertion & Reason */}
      {(mcq.assertion || mcq.reason) && (
        <div className="mb-2">
          {mcq.assertion && <p><span className="font-semibold">Assertion:</span> {mcq.assertion}</p>}
          {mcq.reason && <p><span className="font-semibold">Reason:</span> {mcq.reason}</p>}
        </div>
      )}

      {/* Chronology */}
      {mcq.chronologyItems?.filter(Boolean).length > 0 && (
        <div className="mb-2">
          <span className="font-semibold">Chronology:</span>
          <ol className="list-decimal ml-6 text-base">
            {mcq.chronologyItems.filter(Boolean).map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Correct Answer */}
      {mcq.correctAnswer && (
        <div className="mb-2 text-green-700 dark:text-green-400 font-semibold">
          Correct Answer: {mcq.correctAnswer}
        </div>
      )}

      {/* Explanation */}
      {mcq.explanation && (
        <div className="mb-2">
          <span className="font-semibold">Explanation:</span> {mcq.explanation}
        </div>
      )}

      <div className="mt-3 space-x-4">
        <button
          onClick={() => handleDelete(mcq._id)}
          className="
px-4
py-2
rounded-xl
bg-red-500
hover:bg-red-600
text-white
font-medium
shadow-md
transition
"
        >
          Delete
        </button>
        <button
  onClick={() => {
    setEditMCQ(mcq);
    setShowEditModal(true);
  }}
  className="
px-4
py-2
rounded-xl
bg-blue-500
hover:bg-blue-600
text-white
font-medium
shadow-md
transition
"
>
  Edit
</button>
      </div>
    </div>
  );

  if (loading) return <div className="p-4 text-gray-700 dark:text-gray-300">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-10">
      <h1
  className="
    text-5xl
    font-extrabold
    bg-gradient-to-r
    from-blue-600
    to-purple-600
    bg-clip-text
    text-transparent
    mb-10
  "
>
  MCQ Upload & View
</h1>

      <MCQManager
  onUploadSuccess={fetchGroupedMCQs}
/>

{/* CALENDAR VIEW */}

<div
  className="
    rounded-3xl
    border
    border-gray-200
    dark:border-gray-700
    bg-white/90
    dark:bg-gray-800/90
    backdrop-blur-xl
    shadow-xl
    p-6
  "
>

  <div className="mb-6">

    <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
      MCQ Upload Calendar
    </h2>

    <p className="text-gray-500 dark:text-gray-400 mt-2">
      Dates with uploaded MCQs are highlighted.
    </p>

  </div>

  <Calendar
    value={selectedDate}
    onChange={setSelectedDate}

    tileContent={({ date, view }) => {
      if (
        view === "month" &&
        uploadedDates.includes(date.toDateString())
      ) {
        return (
          <div className="flex justify-center mt-1">
            <div className="h-2 w-2 bg-green-500 rounded-full" />
          </div>
        );
      }
    }}

    className="w-full rounded-2xl overflow-hidden border-none"
  />

</div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-10 mb-4">MCQs by Date</h2>

      {Object.keys(groupedData).length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No MCQs available.</p>
      ) : (
        Object.entries(groupedData).map(([year, months]) => (
          <div key={year} className="mb-6">
            <button
              onClick={() => setOpenYear(openYear === year ? null : year)}
              className="
w-full
text-left
rounded-2xl
bg-gradient-to-r
from-blue-600
to-purple-600
text-white
px-6
py-4
font-bold
text-2xl
shadow-lg
hover:scale-[1.01]
transition
"
            >
              {year}
            </button>

            {openYear === year &&
              Object.entries(months).map(([month, days]) => (
                <div key={month} className="ml-6 mt-2">
                  <button
                    onClick={() => setOpenMonth(openMonth === month ? null : month)}
                    className="
w-full
text-left
bg-blue-50
dark:bg-gray-800
border
border-blue-200
dark:border-gray-700
rounded-2xl
px-5
py-3
font-semibold
text-xl
text-blue-700
dark:text-blue-300
shadow-sm
hover:shadow-md
transition
"
                  >
                    {month}
                  </button>

                  {openMonth === month &&
                    Object.entries(days).map(([day, mcqs]) => (
                      <div
  key={day}
  id={new Date(`${month} ${day}, ${year}`).toDateString()}
  className="ml-8 mt-4"
>
                        <button
                          onClick={() => setOpenDay(openDay === day ? null : day)}
                          className="
w-full
text-left
rounded-xl
bg-white
dark:bg-gray-800
border
border-gray-200
dark:border-gray-700
px-4
py-3
font-semibold
text-lg
shadow-sm
hover:bg-blue-50
dark:hover:bg-gray-700
transition
"
                        >
                          {day}
                        </button>

                        {openDay === day && (
  <div className="grid gap-4 mt-4">
    {mcqs.map((mcq) => renderMCQDetails(mcq))}
  </div>
)}
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))
      )}
      {/* EDIT MODAL */}
{showEditModal && editMCQ && (
  <div
  className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
  onClick={() => {
    setShowEditModal(false);
    setEditMCQ(null);
  }}
>

    <div
  className="relative bg-white dark:bg-gray-900 w-full max-w-6xl max-h-[95vh] overflow-y-auto rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700"
  onClick={(e) => e.stopPropagation()}
>

      {/* HEADER */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-t-2xl">

        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Edit MCQ
        </h2>

        <button
          onClick={() => {
            setShowEditModal(false);
            setEditMCQ(null);
          }}
          className="text-3xl leading-none text-gray-500 hover:text-red-500"
        >
          ×
        </button>
      </div>

      {/* BODY */}
      <div className="p-6">
        <MCQManager
          editMCQ={editMCQ}
          onUploadSuccess={() => {
            fetchGroupedMCQs();

            setShowEditModal(false);
            setEditMCQ(null);
          }}
          onEditClear={() => {
            setShowEditModal(false);
            setEditMCQ(null);
          }}
        />
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default MCQGroupedView;
